import {
  Entities_Login_StateInfo_Response,
  Entities_Modules_SlugLink,
  TeamId
} from '@suntex.fim/api-client'
import { GoalsIndexPhase, Locale, SpecialGradeLevel } from '@suntex.fim/enums'
import { currentFimYear } from '@suntex.fim/utils'

import { ProfileState } from '../hooks/useProfile'

import { getLocale } from './getLocale'

function storageGet<T>(storage: Storage, key: string, defaultValue: T): T {
  const raw = storage.getItem(key)
  if (raw === null) return defaultValue
  try {
    return JSON.parse(raw) as T
  } catch (e) {
    console.warn(`Failed to parse storage data for key "${key}".`, e)
    return defaultValue
  }
}

function storageSet<T>(storage: Storage, key: string, value: T): void {
  if (value === undefined) {
    storage.removeItem(key)
    return
  }
  storage.setItem(key, JSON.stringify(value))
}

class AppSessionStorage {
  get vifLevel(): number {
    return storageGet(sessionStorage, 'vifLevel', 0)
  }
  set vifLevel(value: number) {
    storageSet(sessionStorage, 'vifLevel', value)
  }

  get bonusLevel(): number {
    return storageGet(sessionStorage, 'bonusLevel', 0)
  }
  set bonusLevel(value: number) {
    storageSet(sessionStorage, 'bonusLevel', value)
  }

  get ctLevel(): number {
    return storageGet(sessionStorage, 'ctLevel', 0)
  }
  set ctLevel(value: number) {
    storageSet(sessionStorage, 'ctLevel', value)
  }

  get gymLevel(): number {
    return storageGet(sessionStorage, 'gymLevel', 0)
  }
  set gymLevel(value: number) {
    storageSet(sessionStorage, 'gymLevel', value)
  }

  get jtfLevel(): number {
    return storageGet(sessionStorage, 'jtfLevel', 0)
  }
  set jtfLevel(value: number) {
    storageSet(sessionStorage, 'jtfLevel', value)
  }

  get jtfWhen(): number {
    return storageGet(sessionStorage, 'jtfWhen', 0)
  }
  set jtfWhen(value: number) {
    storageSet(sessionStorage, 'jtfWhen', value)
  }

  get wdykLevel(): number {
    return storageGet(sessionStorage, 'wdykLevel', 0)
  }
  set wdykLevel(value: number) {
    storageSet(sessionStorage, 'wdykLevel', value)
  }

  get jtfOperation(): number {
    return storageGet(sessionStorage, 'jtfOperation', 1)
  }
  set jtfOperation(value: number) {
    storageSet(sessionStorage, 'jtfOperation', value)
  }

  get kasLevel(): number {
    return storageGet(sessionStorage, 'kasLevel', 0)
  }
  set kasLevel(value: number) {
    storageSet(sessionStorage, 'kasLevel', value)
  }

  get goalsIndexPhase(): GoalsIndexPhase {
    return storageGet(
      sessionStorage,
      'goalsIndexPhase',
      GoalsIndexPhase.Elementary
    )
  }
  set goalsIndexPhase(value: GoalsIndexPhase) {
    storageSet(sessionStorage, 'goalsIndexPhase', value)
  }

  get goalsIndexPhaseLoginId(): number {
    return storageGet(sessionStorage, 'goalsIndexPhaseLoginId', 0)
  }
  set goalsIndexPhaseLoginId(value: number) {
    storageSet(sessionStorage, 'goalsIndexPhaseLoginId', value)
  }

  get stickerActivityView(): number {
    return storageGet(sessionStorage, 'stickerActivityView', 0)
  }
  set stickerActivityView(value: number) {
    storageSet(sessionStorage, 'stickerActivityView', value)
  }

  get earlyLevel(): number {
    return storageGet(sessionStorage, 'earlyLevel', 0)
  }
  set earlyLevel(value: number) {
    storageSet(sessionStorage, 'earlyLevel', value)
  }

  get rankingGrade(): number | undefined {
    return storageGet<number | undefined>(
      sessionStorage,
      'rankingGrade',
      undefined
    )
  }
  set rankingGrade(value: number | undefined) {
    storageSet(sessionStorage, 'rankingGrade', value)
  }

  get rankingLevel(): number | undefined {
    return storageGet<number | undefined>(
      sessionStorage,
      'rankingLevel',
      undefined
    )
  }
  set rankingLevel(value: number | undefined) {
    storageSet(sessionStorage, 'rankingLevel', value)
  }

  get rankingYear(): string {
    return storageGet(sessionStorage, 'rankingYear', '')
  }
  set rankingYear(value: string) {
    storageSet(sessionStorage, 'rankingYear', value)
  }

  get schoolYear(): string {
    return storageGet(
      sessionStorage,
      'schoolYear',
      currentFimYear({ now: new Date(), isIndia: getLocale() === Locale.IN })
    )
  }
  set schoolYear(value: string) {
    storageSet(sessionStorage, 'schoolYear', value)
  }

  get assessmentGrade(): SpecialGradeLevel {
    return storageGet(
      sessionStorage,
      'assessmentGrade',
      SpecialGradeLevel.AllGrades
    )
  }
  set assessmentGrade(value: SpecialGradeLevel) {
    storageSet(sessionStorage, 'assessmentGrade', value)
  }

  get slugLinks(): Entities_Modules_SlugLink[] {
    return storageGet<Entities_Modules_SlugLink[]>(
      sessionStorage,
      'slugLinks',
      []
    )
  }
  set slugLinks(value: Entities_Modules_SlugLink[]) {
    storageSet(sessionStorage, 'slugLinks', value)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get dashboardConfigData(): any {
    return storageGet<unknown>(sessionStorage, 'dashboardConfigData', undefined)
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set dashboardConfigData(value: any) {
    storageSet(sessionStorage, 'dashboardConfigData', value)
  }

  get profileCache(): { data: ProfileState; lastFetched: number } | undefined {
    return storageGet<{ data: ProfileState; lastFetched: number } | undefined>(
      sessionStorage,
      'profileCache',
      undefined
    )
  }
  set profileCache(
    value: { data: ProfileState; lastFetched: number } | undefined
  ) {
    storageSet(sessionStorage, 'profileCache', value)
  }

  get stickerCount(): number | undefined {
    return storageGet<number | undefined>(
      sessionStorage,
      'stickerCount',
      undefined
    )
  }
  set stickerCount(value: number | undefined) {
    storageSet(sessionStorage, 'stickerCount', value)
  }

  get login(): Entities_Login_StateInfo_Response | undefined {
    return storageGet<Entities_Login_StateInfo_Response | undefined>(
      sessionStorage,
      'login',
      undefined
    )
  }
  set login(value: Entities_Login_StateInfo_Response | undefined) {
    storageSet(sessionStorage, 'login', value)
  }

  get token(): string | undefined {
    return storageGet<string | undefined>(sessionStorage, 'token', undefined)
  }
  set token(value: string | undefined) {
    storageSet(sessionStorage, 'token', value)
  }
}

export const appSessionStorage = new AppSessionStorage()
